import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Shield, MessageSquare, Calendar, Globe, Lock, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { ClipLoader } from 'react-spinners';
import { profileApi, updateProfileApi, changePasswordApi } from '../../Api/BaseApi';
import { useTheme } from '../../Context/ThemeContext';
import { getTimeZones } from '../../utils/timezones';
import './index.css';

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

// The editable fields, normalized so dirty-checking is a simple compare.
const editableFrom = (p) => ({
  first_name: p?.first_name || '',
  last_name: p?.last_name || '',
  time_zone: p?.time_zone || '',
});

// Lightweight in-page toast/banner (no extra dependency). Auto-dismisses success.
const Toast = ({ toast, onClose }) => {
  useEffect(() => {
    if (toast && toast.type === 'ok') {
      const t = setTimeout(onClose, 4000);
      return () => clearTimeout(t);
    }
  }, [toast, onClose]);
  if (!toast) return null;
  const Icon = toast.type === 'ok' ? CheckCircle2 : AlertCircle;
  return (
    <div className={`settings-toast ${toast.type}`} role="status" aria-live="polite">
      <Icon size={16} aria-hidden="true" />
      <span className="settings-toast-text">{toast.text}</span>
      <button type="button" className="settings-toast-close" onClick={onClose} aria-label="Dismiss">
        <X size={14} />
      </button>
    </div>
  );
};

const Settings = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);

  // Editable form state + the saved baseline we diff against for "dirty".
  const [form, setForm] = useState({ first_name: '', last_name: '', time_zone: '' });
  const [baseline, setBaseline] = useState({ first_name: '', last_name: '', time_zone: '' });
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [toast, setToast] = useState(null);

  // Password change (unchanged behavior).
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const timezones = useMemo(() => getTimeZones(), []);

  useEffect(() => {
    profileApi().then((p) => {
      setProfile(p);
      const e = editableFrom(p);
      setForm(e);
      setBaseline(e);
    }).catch(() => setToast({ type: 'error', text: 'Could not load your profile.' }));
  }, []);

  const isDirty = (
    form.first_name !== baseline.first_name ||
    form.last_name !== baseline.last_name ||
    form.time_zone !== baseline.time_zone
  );

  // Client-side validation: first and last name must not be blank.
  const validate = (f) => {
    const errs = {};
    if (!f.first_name.trim()) errs.first_name = 'First name is required.';
    if (!f.last_name.trim()) errs.last_name = 'Last name is required.';
    return errs;
  };

  // Warn before leaving (tab close / reload) only while there are unsaved edits.
  const dirtyRef = useRef(isDirty);
  dirtyRef.current = isDirty;
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const setField = (k) => (e) => {
    const next = { ...form, [k]: e.target.value };
    setForm(next);
    // Clear a field's error as soon as the user corrects it.
    if (fieldErrors[k]) setFieldErrors((fe) => ({ ...fe, [k]: undefined }));
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!isDirty || saving) return;
    const errs = validate(form);
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }
    setFieldErrors({});
    setToast(null);
    setSaving(true);
    // Send only the changed fields.
    const changes = {};
    ['first_name', 'last_name', 'time_zone'].forEach((k) => {
      if (form[k] !== baseline[k]) changes[k] = (k === 'time_zone') ? form[k] : form[k].trim();
    });
    try {
      const res = await updateProfileApi(changes);
      const updated = res.profile || { ...profile, ...changes };
      setProfile(updated);
      const e2 = editableFrom(updated);
      setForm(e2);
      setBaseline(e2); // new clean baseline -> Save disabled, beforeunload off
      setToast({ type: 'ok', text: res.message || 'Profile updated successfully' });
    } catch (err) {
      const data = err.response?.data;
      if (data?.errors) setFieldErrors(data.errors);
      const fieldErr = data?.errors && Object.values(data.errors)[0];
      setToast({ type: 'error', text: fieldErr || data?.detail || 'Could not save your profile. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const cancelEdits = () => {
    setForm(baseline);
    setFieldErrors({});
    setToast(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (nw !== confirm) { setMsg({ type: 'error', text: 'New passwords do not match.' }); return; }
    if (nw.length < 8) { setMsg({ type: 'error', text: 'New password must be at least 8 characters.' }); return; }
    setBusy(true);
    try {
      await changePasswordApi(cur, nw);
      setMsg({ type: 'ok', text: 'Password changed. Please log in again.' });
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || 'Could not change password.' });
    } finally {
      setBusy(false);
    }
  };

  const accountRows = profile ? [
    { icon: <Shield size={16} />, label: 'Role', value: profile.role },
    { icon: <MessageSquare size={16} />, label: 'Enquiries made', value: String(profile.enquiries_count ?? 0) },
    { icon: <Calendar size={16} />, label: 'Member since', value: fmtDate(profile.member_since) },
  ] : [];

  return (
    <div className={`settings-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <header className="settings-header">
        <h1>Settings</h1>
        <p className="settings-sub">Your profile and account security.</p>
      </header>

      <Toast toast={toast} onClose={() => setToast(null)} />

      {/* --- Profile Information ------------------------------------------- */}
      <section className="settings-card" aria-labelledby="profile-info-heading">
        <div className="card-head">
          <h2 id="profile-info-heading">Profile Information</h2>
          <p className="card-desc">Update your personal details and regional preferences.</p>
        </div>

        {!profile && <p className="settings-muted">Loading…</p>}
        {profile && (
          <form className="profile-form" onSubmit={saveProfile} noValidate>
            <div className="field-grid">
              <div className="field">
                <label htmlFor="pf-first">First name</label>
                <input
                  id="pf-first" type="text" value={form.first_name}
                  onChange={setField('first_name')} placeholder="First name"
                  maxLength={150} autoComplete="given-name"
                  aria-invalid={!!fieldErrors.first_name}
                  aria-describedby={fieldErrors.first_name ? 'pf-first-err' : undefined}
                  className={fieldErrors.first_name ? 'has-error' : ''}
                />
                {fieldErrors.first_name && (
                  <span id="pf-first-err" className="field-error" role="alert">{fieldErrors.first_name}</span>
                )}
              </div>

              <div className="field">
                <label htmlFor="pf-last">Last name</label>
                <input
                  id="pf-last" type="text" value={form.last_name}
                  onChange={setField('last_name')} placeholder="Last name"
                  maxLength={150} autoComplete="family-name"
                  aria-invalid={!!fieldErrors.last_name}
                  aria-describedby={fieldErrors.last_name ? 'pf-last-err' : undefined}
                  className={fieldErrors.last_name ? 'has-error' : ''}
                />
                {fieldErrors.last_name && (
                  <span id="pf-last-err" className="field-error" role="alert">{fieldErrors.last_name}</span>
                )}
              </div>

              <div className="field field-wide">
                <label htmlFor="pf-tz">Time zone</label>
                <input
                  id="pf-tz" type="text" list="iana-timezones" value={form.time_zone}
                  onChange={setField('time_zone')} spellCheck={false} autoComplete="off"
                  placeholder="Search a time zone (e.g. Asia/Dubai)"
                  aria-describedby="pf-tz-help"
                  aria-invalid={!!fieldErrors.time_zone}
                  className={fieldErrors.time_zone ? 'has-error' : ''}
                />
                <datalist id="iana-timezones">
                  {timezones.map((tz) => <option key={tz} value={tz} />)}
                </datalist>
                <span id="pf-tz-help" className="field-help">
                  <Globe size={13} aria-hidden="true" /> Used for report timestamps and notification timing.
                </span>
                {fieldErrors.time_zone && (
                  <span className="field-error" role="alert">{fieldErrors.time_zone}</span>
                )}
              </div>

              {/* Read-only email — intentionally locked, visually distinct. */}
              <div className="field field-wide">
                <label htmlFor="pf-email">Email</label>
                <div className="locked-input" id="pf-email">
                  <Mail size={15} aria-hidden="true" />
                  <span className="locked-value">{profile.email}</span>
                  <span className="locked-tag"><Lock size={12} aria-hidden="true" /> Locked</span>
                </div>
              </div>
            </div>

            <div className="profile-actions">
              <button type="button" className="btn btn-ghost" onClick={cancelEdits} disabled={!isDirty || saving}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={!isDirty || saving}>
                {saving && <ClipLoader color="#ffffff" size={14} cssOverride={{ marginRight: 8 }} />}
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
      </section>

      {/* --- Account details (read-only) ---------------------------------- */}
      {profile && (
        <section className="settings-card" aria-labelledby="account-heading">
          <div className="card-head">
            <h2 id="account-heading">Account</h2>
            <p className="card-desc">Managed by your administrator.</p>
          </div>
          <div className="profile-grid">
            {accountRows.map((r, i) => (
              <div className="profile-row" key={i}>
                <span className="profile-label">{r.icon}{r.label}</span>
                <span className="profile-value">{r.value}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* --- Change Password ---------------------------------------------- */}
      <section className="settings-card" aria-labelledby="password-heading">
        <div className="card-head">
          <h2 id="password-heading">Change Password</h2>
        </div>
        <form className="pw-form" onSubmit={submit}>
          <label>Current password
            <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required />
          </label>
          <label>New password
            <input type="password" value={nw} onChange={(e) => setNw(e.target.value)} required />
          </label>
          <label>Confirm new password
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
          </label>
          {msg && <p className={`pw-msg ${msg.type}`}>{msg.text}</p>}
          <button type="submit" disabled={busy} className="btn btn-primary">
            {busy ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </section>
    </div>
  );
};

export default Settings;
